// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the AYNI chatbot's conversation, run in the page (AYNI 3D's
 * `docs/AI_CHATBOT_PLAN.md` §10.2). The engine is AYNI 3D's own
 * (`src/ayni3d/main/ai/ai-orchestrator.ts`, copied unchanged). Here it
 * gets this site's surface (`'web'`), its commands, its executor, and one
 * endpoint: the site's own guarded route, which holds no key in the page.
 *
 * AYNI (2026-10-07, AYNI 3D's research and education contract §2): before
 * each question the page asks the server who the chatbot is helping
 * (`GET /api/ayni-chat/mode`): a researcher, anyone else, or no mode (a
 * server from before the modes, or with them switched off). It sends the
 * environment it is in (`X-Ayni-Chat-Env`) on that request and on every
 * model round, so the server resolves the same mode for both. The turn then
 * runs with that mode's instructions and commands. A change of mode starts
 * a fresh history (the engine's rule).
 */
import { AiOrchestrator } from '../ayni3d/main/ai/ai-orchestrator'
import { validateAiChatSendRequest, type AiChatErrorCode, type AiChatEvent } from '../ayni3d/shared/ai-chat-protocol'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'
import type { AgentCommand } from '../ayni3d/shared/protocol'
import { LogLevel, type Logger } from '../ayni3d/shared/logger/types'
import { streamChatCompletion } from '../ayni3d/main/ai/openai-chat-client'
import { logger as appLogger } from '../utils/logger'
import {
  AYNI_CHAT_BASE_URL,
  NO_MODE,
  XR_CHAT_COMMANDS,
  XR_CHAT_ENV_HEADER,
  xrChatCommands,
  type XrChatEnvClaim,
  type XrChatModeInfo,
} from './xrChatScope'
import { XrCatalogSearch, XrChatExecutor, type XrChatHost } from './xrChatExecutor'
import { featuresSituation } from './xrChatFeatures'
import { satellitesSituation } from './xrChatSatellites'

/** The engine's logger, on the site's: never the visitor's words, only what the engine itself logs. */
function engineLogger(): Logger {
  const self: Logger = {
    debug: (message, data) => appLogger.debug(message, data),
    info: (message, data) => appLogger.debug(message, data),
    warn: (message, data) => appLogger.warn(message, data),
    error: (message, error) => appLogger.error(message, error),
    setLevel: () => {},
    getLevel: () => LogLevel.INFO,
    withCategory: () => self,
  }
  return self
}

export interface AyniChatSession {
  /** Send one question; events stream to `emit`: text, actions, then one done or error. */
  send(text: string, language: AiChatLanguage, emit: (event: AiChatEvent) => void): Promise<void>
  /** Stop the answer in progress. */
  cancel(): void
  /** Start a new conversation. */
  reset(): void
}

/** Where the page is, for the server's mode (`main.ts` answers). */
export interface AyniChatEnvironment {
  /** The environment the page is in: research, education or general. */
  claim(): XrChatEnvClaim
}

/** What `GET /api/ayni-chat/mode` said: the mode, or why the chatbot is not open to this page. */
export type AyniChatAccessAnswer = ({ access: 'ok' } & XrChatModeInfo & { commands: AgentCommand['type'][] }) | { access: 'disabled' | 'not_configured' | 'unauthorized' | 'plan_required' }

let sequence = 0
/** The turn's extra context: what the globe draws from records, and the satellites the data comes from, when it does. */
function situationOf(host: XrChatHost, language: string): { situation?: string } {
  const lines = [featuresSituation(host.features?.() ?? null), satellitesSituation(host.satellites ?? null, language)].filter((l): l is string => !!l)
  return lines.length ? { situation: lines.join(' ') } : {}
}

const newId = (prefix: string): string => `${prefix}-${Date.now().toString(36)}-${(sequence++).toString(36)}`

/** The headers that tell the server where this page is. */
export function environmentHeaders(env: AyniChatEnvironment | undefined): Record<string, string> {
  return env ? { [XR_CHAT_ENV_HEADER]: env.claim() } : {}
}

/** Ask the server who the chatbot is helping. A server from before the modes (or none) answers no mode. */
export async function fetchChatMode(fetchImpl: typeof fetch, headers: Record<string, string>): Promise<AyniChatAccessAnswer> {
  try {
    const res = await fetchImpl(`${AYNI_CHAT_BASE_URL}/mode`, { headers: { Accept: 'application/json', ...headers }, credentials: 'same-origin' })
    if (!res.ok) return { access: 'ok', ...NO_MODE, commands: [...XR_CHAT_COMMANDS] }
    const body = (await res.json()) as Record<string, unknown>
    if (body.access !== 'ok') {
      const access = body.access
      return access === 'disabled' || access === 'not_configured' || access === 'unauthorized' || access === 'plan_required'
        ? { access }
        : { access: 'not_configured' }
    }
    const mode = body.mode === 'general' || body.mode === 'research' || body.mode === 'teacher' || body.mode === 'student' ? body.mode : null
    const info: XrChatModeInfo = { mode, socratic: body.socratic !== false, research: body.research === true }
    // The server's list, kept to commands this page knows for that mode.
    const known = xrChatCommands(info)
    const commands = Array.isArray(body.commands) ? (body.commands as unknown[]).filter((c): c is AgentCommand['type'] => typeof c === 'string' && known.has(c as AgentCommand['type'])) : [...known]
    return { access: 'ok', ...info, commands }
  } catch {
    return { access: 'ok', ...NO_MODE, commands: [...XR_CHAT_COMMANDS] }
  }
}

/** The engine error a refusal of `/mode` becomes; the panel words it (`ayniChatPanel.errorText`). */
const REFUSAL: Record<Exclude<AyniChatAccessAnswer['access'], 'ok'>, { code: AiChatErrorCode; message: string }> = {
  disabled: { code: 'AI_DISABLED', message: 'The assistant is switched off.' },
  not_configured: { code: 'AI_UNAVAILABLE', message: 'The assistant is not set up on this site.' },
  unauthorized: { code: 'AI_NOT_CONFIGURED', message: 'The assistant endpoint answered 401.' },
  plan_required: { code: 'AI_UNAVAILABLE', message: 'The assistant endpoint answered 402.' },
}

/** One conversation with the chatbot, on this page's globe. */
export function createAyniChat(host: XrChatHost, fetchImpl?: typeof fetch, env?: AyniChatEnvironment): AyniChatSession {
  const catalog = new XrCatalogSearch(() => host.datasets())
  const executor = new XrChatExecutor(host, catalog)
  const baseFetch: typeof fetch = fetchImpl ?? ((input, init) => fetch(input, init))
  // Every model round carries where the page is, so the server resolves the same mode as /mode did.
  const withEnvironment: typeof fetch = (input, init) => {
    const headers = new Headers(init?.headers)
    for (const [k, v] of Object.entries(environmentHeaders(env))) headers.set(k, v)
    return baseFetch(input, { ...init, headers })
  }
  const engine = new AiOrchestrator({
    logger: engineLogger(),
    isEnabled: async () => true,
    resolveEndpoint: async () => ({ ok: true, endpoint: { baseUrl: AYNI_CHAT_BASE_URL, model: '', apiKey: null } }),
    executeAgentCommand: (command) => executor.execute(command),
    searchCatalog: async (query, limit) => catalog.search(query, limit),
    streamChat: (options) => streamChatCompletion({ ...options, fetchImpl: withEnvironment }),
  })
  let sessionId = newId('web')
  return {
    async send(text, language, emit) {
      const checked = validateAiChatSendRequest({ sessionId, turnId: newId('turn'), text, language })
      if (!checked.ok) {
        emit({ type: 'error', sessionId, turnId: 'invalid', code: 'AI_INVALID_REQUEST', message: checked.message })
        return
      }
      const attempt = async (retrying: boolean): Promise<void> => {
        // No environment (tests, an embed without one): today's behaviour.
        const answer = env ? await fetchChatMode(baseFetch, environmentHeaders(env)) : { access: 'ok' as const, ...NO_MODE, commands: [...XR_CHAT_COMMANDS] }
        if (answer.access !== 'ok') {
          const refusal = REFUSAL[answer.access]
          emit({ type: 'error', sessionId, turnId: checked.request.turnId, code: refusal.code, message: refusal.message })
          return
        }
        const info: XrChatModeInfo = { mode: answer.mode, socratic: answer.socratic, research: answer.research }
        executor.setContext({ mode: info.mode, language })
        // AYNI: the mode can change between /mode and the model round (the licence in use
        // changes); the server then refuses the round (400). Before anything was said, ask the
        // mode again and try once more.
        let said = false
        let mismatch = false
        const relay = (event: AiChatEvent): void => {
          if (!retrying && !said && event.type === 'error' && /\b400\b/.test(event.message)) {
            mismatch = true
            return
          }
          if (event.type !== 'error') said = true
          emit(event)
        }
        await engine.runTurn(
          { ...checked.request, sessionId },
          {
            surface: 'web',
            allowedCommands: new Set(answer.commands),
            // AYNI (DSA 1.8): the records the globe draws, so the model reads them rather than the colours;
            // and the satellites the data comes from, so it asks satellites_sources.
            ...situationOf(host, language),
            ...(info.mode ? { mode: info.mode, socratic: info.socratic } : {}),
          },
          env ? relay : emit,
        )
        if (mismatch) {
          engine.reset('web', sessionId)
          sessionId = newId('web')
          await attempt(true)
        }
      }
      await attempt(false)
    },
    cancel() {
      engine.cancel('web', sessionId)
    },
    reset() {
      engine.reset('web', sessionId)
      sessionId = newId('web')
    },
  }
}
